import { isFunction } from '@lit-element/utils'
import { isClient } from '@vueuse/core'

export function isDefined<T = unknown>(
  value: T | undefined | null
): value is Exclude<T, undefined | null> {
  return value !== undefined && value !== null
}

/**
 * 根据数组元素中某个或多个属性的值转换为映射对象
 *
 * @param list 需要被转换的数组
 * @param prop 需要被转换的属性或提供一个读取方法
 * @param accessor 映射的值的读取方法，默认返回元素本身
 * @param isMap 是否使用 Map 对象储存结果
 *
 * @returns 转换后的映射对象
 */
export function listToMap<T = any, O = T>(
  list: T[],
  prop: keyof T | ((item: T) => any),
  accessor?: (item: T) => O,
  isMap?: false
): Record<string, O>
export function listToMap<T = any, O = T, K extends keyof T = keyof T>(
  list: T[],
  prop: K,
  accessor?: (item: T) => O,
  isMap?: true
): Map<T[K], O>
export function listToMap<T = any, O = T, K = any>(
  list: T[],
  prop: (item: T) => K,
  accessor?: (item: T) => O,
  isMap?: true
): Map<K extends keyof T ? T[K] : unknown, O>
export function listToMap<T = any, O = T>(
  list: T[],
  prop: keyof T | ((item: T) => any),
  accessor: (item: T) => O = (v) => v as any,
  isMap = false
) {
  const map = (isMap ? new Map<string, any>() : {}) as any

  if (!isDefined(prop)) return map

  const set = isMap
    ? (key: any, value: O) => map.set(key, value)
    : (key: any, value: O) => (map[key] = value)
  const propAccessor = isFunction(prop) ? prop : (item: T) => item[prop]

  list.forEach((item) => {
    const key = propAccessor(item)

    if (isDefined(key)) {
      set(key, accessor(item))
    }
  })

  return map
}

export function getStepByWord(wordTemplate: string, step: number) {
  return wordTemplate.replace(/{n}/g, `${step}`)
}

export function boundRange(
  number: number | string,
  min: number,
  max: number
): number {
  return Math.max(min, Math.min(max, Number.parseFloat(number as string)))
}

/**
 * 为给定的整数开头填充 0，直至满足指定的长度
 *
 * @param number 需要处理的整数
 * @param length 填充至的长度
 *
 * @returns 填充后的字面值
 */
export function padStartZeros(number: number, length: number) {
  if (length <= 0) {
    return number.toString()
  }

  return `${number < 0 ? '-' : ''}${String(
    Math.abs(Math.round(number))
  ).padStart(length, '0')}`
}

/**
 * 将小于 10 整数 N 变成 `0N` 的字符串，方法不会对入参校验
 *
 * @param number 需要处理的整数
 *
 * @returns 填充后的字面值
 */
export function doubleDigits(number: number) {
  return padStartZeros(number, 2)
}

/**
 * 将给定的被除数和除数，不断的取余直至达到次数限制或余数小于除数，返回系列余数
 *
 * @param number 被除数，需大于 0
 * @param divideBy 除数，需大于 1
 * @param limit 次数限制，默认为 0，小于 1 则不作限制
 *
 * @returns 相除过程的系列余数
 */
export function leaveNumber(number: number, divideBy: number, limit = 0) {
  if (number <= 0 || divideBy <= 1) return [number]

  if (limit < 1) {
    limit = Number.POSITIVE_INFINITY
  }

  const remainders: number[] = []
  let count = 0

  while (number >= divideBy && count < limit) {
    remainders.push(number % divideBy)
    number = Math.floor(number / divideBy)
    ++count
  }

  remainders.push(number)

  return remainders.reverse()
}

export function formatSeconds(seconds: number) {
  if (seconds <= 0) return '00:00'

  const remainders = leaveNumber(Math.ceil(seconds), 60, 2)

  if (remainders.length < 2) {
    remainders.unshift(0)
  }

  return remainders.length < 3
    ? remainders.map(doubleDigits).join(':')
    : remainders.map((r, i) => (i ? doubleDigits(r) : r)).join(':')
}

export function mergeIconScale(scale: number, icon: any) {
  return {
    ...icon,
    scale: +(icon.scale || 1) * scale,
  }
}

/**
 * 获取元素横向的内边距像素值
 *
 * @param el 指定的元素
 *
 * @returns 元素横向的内边距像素值
 */
export function getXPadding(el: HTMLElement | null) {
  if (!isClient || !el) return 0

  const computedStyle = getComputedStyle(el)
  const padding =
    Number.parseFloat(computedStyle.paddingLeft) +
    Number.parseFloat(computedStyle.paddingRight)

  return padding || 0
}

/**
 * 获取元素的选中宽度
 *
 * @param el 指定的元素
 *
 * @returns 元素的选中宽度
 */
export function getRangeWidth(el: HTMLElement | null) {
  if (!isClient || !el) return 0

  const range = document.createRange()

  range.setStart(el, 0)
  range.setEnd(el, el.childNodes.length)

  const rangeWidth = range.getBoundingClientRect().width
  const xPadding = getXPadding(el)

  return rangeWidth + xPadding
}

export const numberRE = /^\s*[+-]?\d*\.?\d+(?:[eE][+-]?\d+)?\s*$/
/**
 * 检测给定的值是否可以通过 parseFlat 或 Number 方法转为数字
 *
 * 开启严格模式则通过正则以更严格的方法判断
 *
 * @param value 需要检测的值
 * @param strict 是否为严格模式
 *
 * @returns 值是否可以转为数字
 */
export function isValidNumber(value: unknown, strict = false) {
  if (typeof value === 'number') {
    return !Number.isNaN(value)
  }

  if (strict) {
    return numberRE.test(String(value))
  }

  return (
    !Number.isNaN(Number.parseFloat(value as string)) ||
    !Number.isNaN(Number(value))
  )
}

/**
 * 将给定的值转成数字，NaN 的情况将会处理成 0
 *
 * @param value 需要转换的值
 *
 * @returns 转换后的数字
 */
export function toNumber(value: unknown) {
  let number = Number.parseFloat(value as string)

  if (Number.isNaN(number)) {
    number = Number(value)
  }

  return Number.isNaN(number) ? 0 : number
}
